import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { ShieldCheck, Edit2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { sortDisclosureRows } from '../sortUtils';

export default function DisclosuresTab({ api, isAdmin, members, user, onChange }) {
  const year = new Date().getFullYear();
  const [list, setList] = useState([]);
  const [yr, setYr] = useState(year);
  const [open, setOpen] = useState(false);
  const [signing, setSigning] = useState(null);
  const [form, setForm] = useState({ has_conflicts: false, conflicts: '', signature: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/board/disclosures?year=${yr}`); setList(data); } catch (e) { /* noop */ }
  }, [api, yr]);
  useEffect(() => { load(); }, [load]);

  const findDisclosure = (mid) => list.find(d => d.member_id === mid && d.year === yr);
  const myMember = members.find(m => m.user_id === user?.id);

  const openSign = (member) => {
    const existing = findDisclosure(member.id);
    setSigning(member);
    setForm({
      has_conflicts: !!existing?.has_conflicts,
      conflicts: existing?.conflicts || '',
      signature: existing?.signature || (user?.name || ''),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.signature.trim()) { toast.error('Please type your name as signature'); return; }
    setBusy(true);
    try {
      await api.post('/board/disclosures', {
        member_id: signing.id, year: yr,
        has_conflicts: form.has_conflicts,
        conflicts: form.has_conflicts ? form.conflicts : '',
        signature: form.signature,
      });
      toast.success('Disclosure signed'); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const missingCount = members.filter(m => !findDisclosure(m.id)).length;

  return (
    <div className="space-y-4" data-testid="disclosures-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#2D1B4E]">Annual Conflict-of-Interest Disclosures</h2>
          <p className="text-xs text-[#5A4D6E]">All directors must sign annually. {missingCount > 0 ? <span className="text-[#D9534F] font-bold">{missingCount} missing for {yr}.</span> : <span className="text-[#43A047] font-bold">All complete for {yr}.</span>}</p>
        </div>
        <Input type="number" value={yr} onChange={e => setYr(parseInt(e.target.value) || year)} className="w-24" data-testid="disclosure-year" />
      </div>

      <Card className="bg-white border-[#DED8EB]">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F4EF] text-[10px] uppercase tracking-wider text-[#8E85A0]">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Director</th>
                <th className="text-left px-3 py-2 font-bold">{yr} Status</th>
                <th className="text-left px-3 py-2 font-bold">Conflicts Reported</th>
                <th className="text-left px-3 py-2 font-bold">Signed At</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sortDisclosureRows(members, findDisclosure).map(m => {
                const d = findDisclosure(m.id);
                const canSign = isAdmin || (myMember && myMember.id === m.id);
                return (
                  <tr key={m.id} className={`border-t border-[#F0EDF6] ${!d ? 'bg-[#FDECEA]' : ''}`} data-testid={`disclosure-row-${m.id}`}>
                    <td className="px-3 py-2 font-medium text-[#2D1B4E]">{m.name}</td>
                    <td className="px-3 py-2">
                      {d ? <Badge className="bg-[#E8F5E9] text-[#1B5E20] border-[#43A047] text-[10px]"><ShieldCheck className="h-2.5 w-2.5 inline mr-1" />Signed</Badge>
                         : <Badge className="bg-[#FDECEA] text-[#D9534F] border-[#D9534F] text-[10px]"><AlertTriangle className="h-2.5 w-2.5 inline mr-1" />Missing</Badge>}
                    </td>
                    <td className="px-3 py-2 text-[#5A4D6E]">{d ? (d.has_conflicts ? <strong className="text-[#E65100]">Yes</strong> : 'None') : '—'}</td>
                    <td className="px-3 py-2 text-[#8E85A0] text-xs">{d?.signed_at?.slice(0, 19).replace('T', ' ') || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {canSign && <Button variant="ghost" size="sm" onClick={() => openSign(m)} className="h-7 text-xs" data-testid={`sign-${m.id}`}>{d ? 'Resign' : 'Sign'}</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sign Disclosure · {yr} · {signing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#5A4D6E]">
              I certify that I have read and agree to abide by The Next Step Programs' Conflict-of-Interest Policy.
              I disclose below any potential conflicts of interest for the year {yr}.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.has_conflicts} onChange={e => setForm(f => ({ ...f, has_conflicts: e.target.checked }))} className="h-4 w-4" data-testid="has-conflicts" />
              <span>I have a potential conflict of interest to disclose</span>
            </label>
            {form.has_conflicts && (
              <div><Label className="text-xs">Describe the conflict(s)</Label>
                <textarea value={form.conflicts} onChange={e => setForm(f => ({ ...f, conflicts: e.target.value }))} rows={3} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" data-testid="conflicts-text" />
              </div>
            )}
            <div><Label className="text-xs">Signature (type your full name) *</Label>
              <Input value={form.signature} onChange={e => setForm(f => ({ ...f, signature: e.target.value }))} className="font-serif italic" data-testid="signature-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#43A047]" data-testid="confirm-sign-btn">{busy ? 'Saving…' : 'Submit Disclosure'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
